import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { FilterRequestDto } from '../dtos/map/filter-request.dto';
import { tileLayer, TileLayerOptions } from 'leaflet';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { MapPayloadRequestInterface } from '../interface/map-payload-request.interface';
import { Format } from '../interface/format.enum';
import { WmsRequestPayloadInterface } from '../interface/wms-request-payload.interface';
import { catchError, map } from 'rxjs/operators';
import { BaseService } from './base.service';
import { SplitFeatureInterface } from '../interface/geobi/split-feature.interface';
import { CropFeatureInterface } from '../interface/geobi/crop-feature.interface';
import { AliasTableInterface } from '../interface/geobi/alias-table.interface';
import { PublicAliasTableInterface } from '../interface/geobi/public-alias-table.interface';
import { EeParamsI } from '../interface/earth-engine.interface';
import {
  AddLayerToMapInterface,
  PublicLayer,
  UpdateLayerStyleInterface,
  FindPublicLayerNearbyFeatureInterface,
  FeatureNearbyAreaOutputInterface,
} from '../interface/geobi/layer.interface';
import { SaveSymbologyPayload } from '../interface/map.interface';
import { ImageMarkerInterface, PointMarkerInterface } from '../interface/point-marker.interface';
import { PathsAppInterface } from '../interface/paths-app.interface';
import { AuthKeycloakService } from './auth-keycloak.service';

@Injectable({
  providedIn: 'root',
})
export class MapService extends BaseService {
  private _mapStates = 'assets/jsons/map-states.json';
  private _mapCounties = 'assets/jsons/map-counties.json';

  private controllerSpatial = 'api/spatial';
  private controllerMurals = 'api/mural';
  private controllerFolders = 'api/folder';
  private controllerCharts = 'api/chart';
  private controllerGeometry = 'api/geometry';
  private controllerProject = 'api/project';
  private controllerForestoken = 'api/forestoken';
  private controllerResumes = 'api/resume';
  private controllerResumeCharts = 'api/resume-chart';
  private controllerRepositories = 'api/repository-manager';
  private controllerRepositoryUpload = 'api/upload-file';
  private controllerGeoUps = 'api/geoups';
  private controllerForms = 'api/forms';
  private controllerUpload = 'api/upload-file';
  private controllerGeoUser = 'api/user';
  private controllerGeoForms = 'api/geoforms';
  private controllerSpatialDocument = 'api/spatial-document';
  private controllerComments = 'api/comments';
  private controllerBalance = 'api/balance';
  private controllerAliasTable = 'api/alias-table';
  private controllerPublicAliasTable = 'api/public-alias-table';
  private controllerPointMarker = 'api/point-marker';
  private controllerMarkerImages = 'api/marker-image';
  private controllerPathsApp = 'api/paths-app';

  constructor(
    private httpClient: HttpClient,
    protected authKeycloakService: AuthKeycloakService
  ) {
    super(authKeycloakService);
  }

  getMap<T>({
    layers,
    queryLayers,
    bbox,
    width,
    height,
    xAxis,
    yAxis,
  }: MapPayloadRequestInterface): Observable<T> {
    const params = new HttpParams({
      fromObject: {
        service: 'WMS',
        version: '1.1.0',
        request: 'GetFeatureInfo',
        layers: layers,
        query_layers: queryLayers,
        bbox: bbox,
        feature_count: 1,
        width: width,
        height: height,
        srs: 'EPSG%3A4326',
        x: xAxis,
        y: yAxis,
      },
    });
    return this.httpClient.get<T>(environment.URL_WMS_BASE, { params: params });
  }

  getTileLayer(payload: WmsRequestPayloadInterface) {
    return tileLayer.wms(environment.URL_WMS_LAYER, {
      layers: payload.layers,
      format: Format.IMAGE_PNG,
      version: environment.TILE_LAYER_VERSION,
      transparent: true,
      attribution: '&copy; Myself',
    } as TileLayerOptions);
  }

  getWmsCqlFilter(payload: WmsRequestPayloadInterface) {
    return tileLayer.wms(environment.URL_WMS_LAYER, {
      layers: payload.layers,
      format: Format.IMAGE_PNG,
      version: environment.TILE_LAYER_VERSION,
      transparent: true,
      cql_filter: payload.cqlFilter,
      styles: payload.styles,
      attribution: '&copy; Myself',
    } as TileLayerOptions);
  }

  salvarFiltros(
    filterRequestDto: FilterRequestDto
  ): Observable<FilterRequestDto> {
    return of(filterRequestDto);
  }

  getStates() {
    return this.httpClient.get(this._mapStates);
  }

  getCounties() {
    return this.httpClient.get(this._mapCounties);
  }

  getSearchSpatial(searchFeaturesForm): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerSpatial}/get_feature/`,
        searchFeaturesForm,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getFolders(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerFolders}/project-folders/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getMurals(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerMurals}/folder-murals/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getProjectInfo(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerProject}/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createGeometry(options: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeometry}/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createMural(options: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerMurals}/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createFolder(options: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerFolders}/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getFolderInfo(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerFolders}/view/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getMuralInfo(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerMurals}/view/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getAllGeometryProject(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerGeometry}/geometries-in-project/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  seeGeometry(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerGeometry}/view/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getFoldersByGeometry(geometryId: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerFolders}/geometry/${geometryId}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getFoldersByTable(tableName: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerFolders}/table/${tableName}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getCountiesByGeometry(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerGeometry}/counties-in-geometry/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteGeometry(geometryId: string): Observable<any> {
    return this.httpClient
      .delete(
        `${this.url}${this.controllerGeometry}/geometries/${geometryId}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  editGeometry(geometryId: string, options: any): Observable<any> {
    return this.httpClient
      .patch(
        `${this.url}${this.controllerGeometry}/geometries/${geometryId}/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getCardsInfo(cardId: number) {
    return this.httpClient
      .get(
        `${this.url}${this.controllerForestoken}/main-info/${cardId}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getMaiProjection(cardId: number, projectionType: string) {
    return this.httpClient
      .get(
        `${this.url}${this.controllerForestoken}/${projectionType}/${cardId}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getAllAreasProject(): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerForestoken}/get-areas/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getFeatureFeicao(searchFeaturesForm): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerSpatial}/get_feature/`,
        searchFeaturesForm,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  updateProject(idProject, projectMapForm: any): Observable<any> {
    return this.httpClient
      .patch(
        `${this.url}${this.controllerProject}/${idProject}/`,
        projectMapForm,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getAllResumesProject(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerResumes}/resume-by_project/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getAllChartsResume(id: number): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerResumeCharts}/resume/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getDataChartResume(options: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerResumeCharts}/get-data/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createChart(options: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerCharts}/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getRepositories(): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerRepositories}/get-my-repositories/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createRepositories(repositorieName: string): Observable<any> {
    const options = {
      name: repositorieName,
    };

    return this.httpClient
      .post(
        `${this.url}${this.controllerRepositories}/create-repository/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // Repositorios-Tabelas_Espaciais - Geo_tables
  getRepositoriesGeoTablesList(repositoryId: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerRepositories}/geo_tables/repository/${repositoryId}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // Repositorios-Nao-Tabelas_Espaciais - tables/repository
  getRepositoriesTablesList(repositoryId: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerRepositories}/tables/repository/${repositoryId}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getAllRepositoriesTables(repositoryId: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerRepositories}/tables/repository/${repositoryId}/all`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getTablesUser(): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerRepositories}/tables-user/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getTableByName(table_name: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerRepositories}/table/${table_name}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getTableById(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerRepositories}/table/id/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // Repositorios-Documentos
  getRepositoriesDocuments(repositoryId: number): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerRepositoryUpload}/file-list/${repositoryId}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // AttributesAnalysis para tabela de camadas
  getAttributesAnalysis(tableName): Observable<any> {
    const options = {
      table_name: `${tableName}`,
      limit: 200,
    };

    return this.httpClient
      .post(
        `${this.url}${this.controllerSpatial}/attributes-analysis/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // Get Geoforms Columns
  getGeoformsColumns(tableName: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerGeoForms}/get-columns/${tableName}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  geoFormsDeleteData(data: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/delete-data/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // Repositorios-Spatial para upload
  sendUploadFileSpatial(data: FormData): Observable<any> {
    // file.append('repository_id', data.repository_id?.toString() || '');

    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/spatial/`,
        data,
        this.nonHeaderContentAuthenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // Repositorios-NonSpatial para upload
  sendUploadData(data, file): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/upload-non-spatial-table/?repository_id=${data.repository_id}`,
        file,
        this.nonHeaderContentAuthenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  updateNonSpatialTableName(
    repository_id: string,
    table_id: string,
    name: string
  ): Observable<any> {
    return this.httpClient
      .patch(
        `${this.url}${this.controllerRepositories}/repository/${repository_id}/update-non-spatial-table-name/${table_id}/`,
        { name: name },
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getFileColumns(file): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/spatial/columns/`,
        file,
        this.nonHeaderContentAuthenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getNoSpatialFileColumns(file): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/file/columns/`,
        file,
        this.nonHeaderContentAuthenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  sendUploadDocument(data, file): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerUpload}/file/?name=${data.name}&repository_id=${data.repository_id}&description=${data.description}`,
        file,
        this.nonHeaderContentAuthenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getUploadFileColumns(file): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/get-file-columns/`,
        file,
        this.nonHeaderContentAuthenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  uploadFileToTable(data, file): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/upload-csv-to-table/?table_name=${data.table_name}&data=${data.data}&column_identity=${data.column_identity}`,
        file,
        this.nonHeaderContentAuthenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createRepositorySpatial(
    createRepositorySpatialSpatiaForm: any,
    columns: any,
    repositoryId: any
  ): Observable<any> {
    const options = {
      name: createRepositorySpatialSpatiaForm.layerName,
      columns: columns,
      repository_id: repositoryId,
      geometry_type: createRepositorySpatialSpatiaForm.layerType,
    };
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/create-repository-table/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  updateRepositoryTable(id: string, data: any): Observable<any> {
    return this.httpClient
      .patch(
        `${this.url}${this.controllerGeoForms}/repository-table/${id}/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getRepositoryTable(id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerGeoForms}/repository-table/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createNewColumn(geoforms_table: string, columsList: any): Observable<any> {
    const options = {
      table_name: geoforms_table,
      columns: columsList,
    };
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/create-column/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createNewColumnForm(table: string, columsList: any): Observable<any> {
    const options = {
      table_name: table,
      columns: columsList,
    };
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/create-column/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteColumnsForm(table: string, columsList: any): Observable<any> {
    const options = {
      table_name: table,
      columns: columsList,
    };
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/delete-columns/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  renameColumns(geoforms_table: string, columsList: any): Observable<any> {
    const options = {
      table_name: geoforms_table,
      columns: columsList,
    };
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/rename-columns/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteColumns(geoforms_table: string, columsList: any): Observable<any> {
    const options = {
      table_name: geoforms_table,
      columns: columsList,
    };
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/delete-columns/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createRepositoryData(data) {
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/create-table/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getGeoformsData(geoDataForm: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/get-data/`,
        geoDataForm,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  downloadGeoformsData(geoDataForm: any): Observable<any> {
    // pega o token de forma síncrona
    const user_token = this.authKeycloakService.getToken();

    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/download-data/`,
        geoDataForm,
        {
          responseType: 'arraybuffer',
          headers: new HttpHeaders({
            Authorization: `Bearer ${user_token?.toString()}`,
          }),
        }
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  splitFeature(data: SplitFeatureInterface): Observable<any> {
    return this.httpClient
      .put(
        `${this.url}${this.controllerGeoForms}/split/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  cropFeature(data: CropFeatureInterface): Observable<any> {
    return this.httpClient
      .put(
        `${this.url}${this.controllerGeoForms}/crop/geometry/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getFormsData(formData: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/get-data/`,
        formData,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getFormsDataCount(formData: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/get-data/count`,
        formData,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  saveFormsData(formData: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/insert-data/`,
        formData,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteFormsData(formData: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerForms}/delete-data/`,
        formData,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  saveGeoformsData(geoDataForm: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/insert-data/`,
        geoDataForm,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  editGeoformsData(geoDataForm: any): Observable<any> {
    return this.httpClient
      .patch(
        `${this.url}${this.controllerGeoForms}/update-data/`,
        geoDataForm,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getProjectToken(): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerGeoUser}/public-user/token/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  sharedProject(
    typeSharedOrUnshared: string,
    projectId: number,
    userId: number
  ): Observable<any> {
    const options = {
      user_id: userId,
      editor: false,
      project_admin: false,
    };

    return this.httpClient
      .post(
        `${this.url}${this.controllerProject}/${projectId}/${typeSharedOrUnshared}/`,
        options,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  shareRepository(dataSharedRepository: any) {
    return this.httpClient
      .post(
        `${this.url}${this.controllerRepositories}/share-repository/`,
        dataSharedRepository,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  bulkUpdateGeoForm(tableName: string, updates: any): Observable<any> {
    const data = {
      table_name: tableName,
      updates: updates,
    };
    return this.httpClient
      .put(
        `${this.url}${this.controllerGeoForms}/bulk-update-data/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  updateForm(
    tableName: string,
    updates: any,
    mf40sysid: number
  ): Observable<any> {
    const data = {
      table_name: tableName,
      value: updates,
      filters: [
        {
          column: 'mf40sysid',
          condition: '=',
          value: mf40sysid,
        },
      ],
    };
    return this.httpClient
      .patch(
        `${this.url}${this.controllerForms}/update-data/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  updateFormMultyLine(
    tableName: string,
    updates: any,
    mf40sysid: any
  ): Observable<any> {
    const filters: any[] = [];
    for (const id in mf40sysid) {
      if (filters.length > 0) {
        filters[filters.length - 1]['operator'] = 'or';
        filters.push({
          column: 'mf40sysid',
          condition: '=',
          value: mf40sysid[id],
        });
      } else {
        filters.push({
          column: 'mf40sysid',
          condition: '=',
          value: mf40sysid[id],
        });
      }
    }
    const data = {
      table_name: tableName,
      value: updates,
      filters: filters,
    };
    return this.httpClient
      .patch(
        `${this.url}${this.controllerForms}/update-data/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  bulkUpdateForm(tableName: string, updates: any): Observable<any> {
    const data = {
      table_name: tableName,
      updates: updates,
    };
    return this.httpClient
      .put(
        `${this.url}${this.controllerForms}/bulk-update-data/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createSpatialDocument(data): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerSpatialDocument}/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteSpatialDocument(documentId: string): Observable<any> {
    return this.httpClient
      .delete(
        `${this.url}${this.controllerSpatialDocument}/${documentId}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getRepoSpatialDocuments(repository_id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerSpatialDocument}/repository/${repository_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getTableSpatialDocuments(table_id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerSpatialDocument}/table/${table_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getFeatureSpatialDocuments(
    table_id: string,
    feature_id: string
  ): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerSpatialDocument}/feature/${table_id}/${feature_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getLayerSpatialDocuments(
    table_name: string,
    mural_id: string,
    repository_id?: string
  ): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerSpatialDocument}/mural/${mural_id}/repository/${repository_id}/table/${table_name}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  removeRepository(repository_id: string) {
    return this.httpClient
      .delete(
        `${this.url}${this.controllerRepositories}/remove-repository/${repository_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteTable(table_name: string, repository_id: string) {
    return this.httpClient
      .delete(
        `${this.url}${this.controllerForms}/delete-table/${table_name}/${repository_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteGeoTable(table_name: string, repository_id: string) {
    return this.httpClient
      .delete(
        `${this.url}${this.controllerGeoForms}/delete-table/${table_name}/${repository_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  createComment(data: any): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerComments}/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getComments(project_id: string): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerComments}/project-comments/${project_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteComment(comment_id: string): Observable<any> {
    return this.httpClient
      .delete(
        `${this.url}${this.controllerComments}/delete/${comment_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  updateComment(comment: any): Observable<any> {
    return this.httpClient
      .patch(
        `${this.url}${this.controllerComments}/update/${comment.id}/`,
        comment,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getEarthEngineUrl(eeParams: EeParamsI): Promise<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerProject}/earth-engine-url/`,
        eeParams,
        this.authenticatedHeader()
      )
      .toPromise();
  }

  getAliasTableColumns(table_name: any): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerAliasTable}/table/${table_name}`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getPublicAliasTableColumns(table_name: string): Observable<PublicAliasTableInterface[]> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerPublicAliasTable}/table/${table_name}`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  updateAliasTableColumns(
    id: string,
    data: AliasTableInterface
  ): Observable<any> {
    return this.httpClient
      .put(
        `${this.url}${this.controllerAliasTable}/${id}`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  addLayerToMap(data: AddLayerToMapInterface): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerGeoForms}/add-layer-to-map/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }


  getAvailableGeoUps(projectId: string) {
    return this.httpClient
      .get(`${this.url}${this.controllerGeoUps}/available/${projectId}`, this.authenticatedHeader())
      .pipe(
        map(this.extractData),
        catchError(this.serviceError)
      );
  }

  // Get Geoforms public layers
  getGeoformsPublicLayers(): Observable<PublicLayer[]> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerGeoForms}/get-public-layers/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  findPublicLayerNearbyFeature(
    data: FindPublicLayerNearbyFeatureInterface
  ): Observable<FeatureNearbyAreaOutputInterface[]> {
    const params = new HttpParams()
      .set('feature_id', data.feature_id)
      .set('table_name', data.table_name)
      .set('area', data.area);

    return this.httpClient
      .get(
        `${this.url}${this.controllerGeoForms}/public-layer/search-nearby-feature/`,
        { params, headers: this.authenticatedHeader().headers }
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  updateLayerStyle(data: UpdateLayerStyleInterface): Observable<any> {
    return this.httpClient
      .patch(
        `${this.url}${this.controllerGeoForms}/update-layer-style/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  saveSymbology(data: SaveSymbologyPayload): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}api/symbologies/`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getSymbologies(project_id: string): Observable<any> {
    const params = new HttpParams()
      .set('project_id', project_id)
      .set('page', '1')
      .set('size', '100')
      .set('include_deactivated', 'false');
    return this.httpClient
      .get(
        `${this.url}api/symbologies/`,
        { ...this.authenticatedHeader(), params }
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  updateSymbology(symbology_id: number, data: SaveSymbologyPayload): Observable<any> {
    return this.httpClient
      .put(
        `${this.url}api/symbologies/${symbology_id}`,
        data,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteSymbology(symbology_id: number): Observable<any> {
    return this.httpClient
      .delete(
        `${this.url}api/symbologies/${symbology_id}`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  deleteLayer(formData: any): Observable<any> {
    return this.httpClient
      .delete(`${this.url}${this.controllerGeoForms}/delete-layer/`, {
        ...this.authenticatedHeader(),
        body: formData,
      })
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // POINT_MARKER ENDPOINTS (GET)
  getPointMarker(id: string): Observable<PointMarkerInterface> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerPointMarker}/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getPointMarkersByProject(
    project_id: string
  ): Observable<PointMarkerInterface[]> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerPointMarker}/project/${project_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // MARKER_IMAGE ENDPOINTS (GET)
  getMarkerImage(id: string): Observable<ImageMarkerInterface> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerMarkerImages}/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getMarkerImagesByPointMarker(
    point_marker_id: string
  ): Observable<ImageMarkerInterface[]> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerMarkerImages}/point-marker/${point_marker_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getMarkerImageFile(id: string): Observable<Blob> {
    return this.httpClient
      .get(`${this.url}${this.controllerMarkerImages}/file/${id}/`, {
        headers: this.authenticatedHeader().headers,
        responseType: 'blob',
      })
      .pipe(catchError(this.serviceError));
  }

  // WALKS_APP ENDPOINTS (POST)
  createPathsApp(walksApp: PathsAppInterface): Observable<any> {
    return this.httpClient
      .post(
        `${this.url}${this.controllerPathsApp}/`,
        walksApp,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  // WALKS_APP ENDPOINTS (GET)
  getPathsAppByProject(project_id: string): Observable<PathsAppInterface[]> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerPathsApp}/project/${project_id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getPathsAppById(id: string): Observable<PathsAppInterface> {
    return this.httpClient
      .get(
        `${this.url}${this.controllerPathsApp}/${id}/`,
        this.authenticatedHeader()
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }
}
